import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import BEM from 'common/bem';
import type {
  ArchivingDeleteSavedSearchResponse,
  ArchivingSavedSearchData,
  ArchivingSavedSearchFilters,
  ArchivingSavedSearchListResponse,
} from 'js-sdk/src/apis/ArchivingAPI';

const classes = BEM.with('SavedSearches');

const TIME_RANGE_PRESET_LABELS: Record<string, string> = {
  last24h: 'Last 24 hours',
  last7d: 'Last 7 days',
  last30d: 'Last 30 days',
  thisQuarter: 'This quarter',
  custom: 'Custom range',
};

/** One-line summary of a saved search's filters, e.g. "SSN OR MRN · Last 7 days · Has attachment". */
function buildFilterSummary(filters: ArchivingSavedSearchFilters): string {
  const parts: string[] = [];
  if (filters.query) parts.push(filters.query);
  if (filters.timeRangePreset) {
    parts.push(TIME_RANGE_PRESET_LABELS[filters.timeRangePreset] || filters.timeRangePreset);
  }
  if (filters.userRoles && filters.userRoles.length > 0) {
    parts.push(
      `${filters.userRoles.length} user/role filter${filters.userRoles.length > 1 ? 's' : ''}`
    );
  }
  if (filters.messageType) parts.push(filters.messageType);
  if (filters.hasAttachment) parts.push('Has attachment');
  return parts.length > 0 ? parts.join(' · ') : 'No filters';
}

type SavedSearchesClient = {
  archiving: {
    getSavedSearches: (
      organizationId: string,
      options?: { mine?: boolean }
    ) => Promise<ArchivingSavedSearchListResponse>;
    deleteSavedSearch: (
      organizationId: string,
      searchId: string
    ) => Promise<ArchivingDeleteSavedSearchResponse>;
  };
};

type SavedSearchesProps = {
  client: SavedSearchesClient | null;
  currentOrganizationId: string;
  /** Only rows created by this user show a delete control — no admin-override permission exists yet. */
  currentUserId: string;
  onRun: (filters: ArchivingSavedSearchFilters) => void;
};

export function SavedSearches({
  client,
  currentOrganizationId,
  currentUserId,
  onRun,
}: SavedSearchesProps) {
  const [savedSearches, setSavedSearches] = useState<ArchivingSavedSearchData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingSearchId, setDeletingSearchId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Ids successfully deleted but not yet reflected in a load that was already in flight when the
  // delete finished — without this, that stale response's list would resurrect the deleted row.
  const recentlyDeletedIds = useRef<Set<string>>(new Set());

  const loadSavedSearches = useCallback(async () => {
    if (!client) {
      // eslint-disable-next-line no-console
      console.warn('[SavedSearches] loadSavedSearches called with no client — skipping');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await client.archiving.getSavedSearches(currentOrganizationId);
      const items = response?.data?.savedSearches || [];
      setSavedSearches(items.filter((item) => !recentlyDeletedIds.current.has(item.searchId)));
    } catch (err) {
      Sentry.logger.error(Sentry.logger.fmt`[SavedSearches] Failed to load saved searches`, {
        error: err,
      });
      setError('Something went wrong loading saved searches. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [client, currentOrganizationId]);

  useEffect(() => {
    loadSavedSearches();
  }, [loadSavedSearches]);

  async function onDelete(searchId: string) {
    if (!client) return;

    setDeletingSearchId(searchId);
    setDeleteError(null);
    try {
      await client.archiving.deleteSavedSearch(currentOrganizationId, searchId);
      recentlyDeletedIds.current.add(searchId);
      setSavedSearches((previous) => previous.filter((item) => item.searchId !== searchId));
    } catch (err) {
      Sentry.logger.error(Sentry.logger.fmt`[SavedSearches] Failed to delete saved search`, {
        error: err,
      });
      setDeleteError('Something went wrong deleting this saved search. Please try again.');
    } finally {
      setDeletingSearchId(null);
    }
  }

  return (
    <div className={classes()}>
      <div className={classes('header')}>
        <div className={classes('header-text')}>
          <h2 className={classes('title')}>Saved Searches</h2>
          <p className={classes('subtitle')}>
            Re-run a previously saved Message Search without re-entering every field
          </p>
        </div>
        <button
          className={classes('refresh-button')}
          data-test-id="saved-searches-refresh"
          disabled={isLoading}
          onClick={() => loadSavedSearches()}
          type="button"
        >
          Refresh
        </button>
      </div>

      {error && <div className={classes('error')}>{error}</div>}
      {deleteError && <div className={classes('error')}>{deleteError}</div>}

      {isLoading && (
        <div className={classes('loading')} data-test-id="saved-searches-loading">
          Loading saved searches…
        </div>
      )}

      {!isLoading && !error && savedSearches.length === 0 && (
        <div className={classes('empty-state')} data-test-id="saved-searches-empty">
          No saved searches yet. Save a search from Message Search to see it here.
        </div>
      )}

      {!isLoading && savedSearches.length > 0 && (
        <ul className={classes('list')} data-test-id="saved-searches-list">
          {savedSearches.map((item) => (
            <li className={classes('item')} key={item.searchId}>
              <button
                className={classes('item-main')}
                data-test-id={`saved-searches-run-${item.searchId}`}
                onClick={() => onRun(item.filters)}
                type="button"
              >
                <span className={classes('item-name')}>{item.name}</span>
                <span className={classes('item-summary')}>{buildFilterSummary(item.filters)}</span>
              </button>
              <div className={classes('item-actions')}>
                <button
                  className={classes('run-button')}
                  data-test-id={`saved-searches-run-button-${item.searchId}`}
                  onClick={() => onRun(item.filters)}
                  type="button"
                >
                  Run
                </button>
                {item.createdByUserId === currentUserId && (
                  <button
                    aria-label="Delete saved search"
                    className={classes('delete-button')}
                    data-test-id={`saved-searches-delete-${item.searchId}`}
                    disabled={deletingSearchId === item.searchId}
                    onClick={() => onDelete(item.searchId)}
                    type="button"
                  >
                    ✕
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
